import React, { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Calendar,
  Phone,
  MapPin
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const UserView = () => {
  const { id } = useParams()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  
  const { getUser } = useFirebase()
  const navigate = useNavigate()

  useEffect(() => {
    loadData()
  }, [id])

  const loadData = async () => {
    try {
      const userData = await getUser(id)
      
      if (!userData) {
        toast.error('User not found')
        navigate('/users')
        return
      }
      
      setUser(userData)
    } catch (error) {
      console.error('Error loading user data:', error)
      toast.error('Failed to load user details')
      navigate('/users')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">User not found</h3>
        <Link to="/users" className="btn-primary">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Link>
      </div>
    )
  }


  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            User: {user.name || user.email}
          </h1>
          <p className="text-sm text-gray-600">
            Detailed user information and activity
          </p>
        </div>
        <Link to="/users" className="btn-outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </Link>
      </div>

      {/* User Info */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <User className="h-5 w-5 mr-2 text-primary-600" />
            User Information
          </h3>
        </div>
        <div className="card-body space-y-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {user.name || 'N/A'}
              </div>
              <div className="text-sm text-gray-500">Name</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Mail className="h-5 w-5 text-gray-400" />
            <div>
              <div className="font-medium text-gray-900">
                {user.email || 'N/A'}
              </div>
              <div className="text-sm text-gray-500">Email</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Phone className="h-5 w-5 text-gray-400" />
            <div>
              <div className="font-medium text-gray-900">
                {user.mobileNumber || 'N/A'}
              </div>
              <div className="text-sm text-gray-500">Mobile Number</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <MapPin className="h-5 w-5 text-gray-400" />
            <div>
              <div className="font-medium text-gray-900">
                {user.country && user.state && user.district 
                  ? `${user.district}, ${user.state}, ${user.country}`
                  : (user.country || user.state || user.district || 'N/A')
                }
              </div>
              <div className="text-sm text-gray-500">Location</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-gray-400" />
            <div>
              <div className="font-medium text-gray-900">
                {(user.createdAt || user.registrationDate)
                  ? new Date(user.createdAt?.seconds ? user.createdAt.seconds * 1000 : (user.createdAt || user.registrationDate)).toLocaleDateString('en-GB')
                  : 'N/A'
                }
              </div>
              <div className="text-sm text-gray-500">Registration Date</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserView
